<script lang="ts">
	import type { InventoryTypesFull$result } from '$houdini'
	import type { Column } from '@isoftdata/svelte-table'
	import type { SvelteAsr } from 'types/common'
	import type { i18n } from 'types/common'

	import { Table, Td } from '@isoftdata/svelte-table'
	import Button from '@isoftdata/svelte-button'
	import InventoryTypeModal from './InventoryTypeModal.svelte'
	import { onMount, tick } from 'svelte'
	import { booleanToYesNo as yesNo } from '@isoftdata/utility-string'
	import { booleanToString } from '@isoftdata/utility-boolean'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import BooleanIndicator from 'components/BooleanIndicator.svelte'
	import { getContext } from 'svelte'

	type InventoryType = InventoryTypesFull$result['inventoryTypes'][number]

	interface Props {
		inventoryTypes: Array<InventoryType>
		selectedInventoryTypeId: number | undefined
		asr: SvelteAsr
		showInactive: boolean
	}

	let {
		inventoryTypes = $bindable(),
		selectedInventoryTypeId = $bindable(),
		asr,
		showInactive = $bindable(),
	}: Props = $props()

	let inventoryTypeModal: InventoryTypeModal | undefined = $state(undefined)

	const { t: translate } = getContext<i18n>('i18next')

	const columns: Array<Column<InventoryType>> = [
		{
			property: 'active',
			name: translate('common:active', 'Active'),
			align: 'center',
			width: '1rem',
			title: translate('configuration.inventorytype.isThisInventoryTypeActive?', 'Is this inventory type active?'),
		},
		{
			property: 'id',
			name: translate('common:typeNum', 'Type #'),
			numeric: true,
			title: translate('configuration.inventorytype.aUniqueForThisInventoryType', 'A unique # for this inventory type'),
		},
		{
			property: 'setId',
			name: translate('common:setNum', 'Set #'),
			numeric: true,
			title: translate(
				'configuration.inventorytype.setidColumnTitle',
				`A 'set' inventory type dictates which type's manufacturer/model list to use on search as well as in the assembly area on the parts screen.\r\nGenerally, components will have a major assembly as its set type and major assemblies will just use their own type #`,
			),
		},
		{
			property: 'name',
			name: translate('common:name', 'Name'),
			title: translate(
				'configuration.inventorytype.whatInventoryOfThisTypeIsCalledMustBeUnique)',
				'What inventory of this type is called (must be unique)',
			),
		},
		{
			property: 'vehicle',
			name: translate('common:vehicleUnit', 'Vehicle Unit'),
			align: 'center',
			width: '1rem',
			title: translate(
				'configuration.inventorytype.ifCheckedThisInventoryTypeRepresentsAVehicleWholeUnitType.',
				'If checked, this inventory type represents a vehicle/whole unit type.',
			),
		},
		{
			property: 'taxCode',
			name: translate('common:taxCode', 'Tax Code'),
			title: translate(
				'configuration.inventorytype.theTaxCodeToUseForThisInventoryTypeForExternalTaxCalculations.',
				'The tax code to use for this inventory type for external tax calculations.',
			),
		},
	]

	const filteredInventoryTypes: Array<InventoryType> = $derived(
		inventoryTypes.filter(inventoryType => showInactive || inventoryType.active),
	)

	const selectedInventoryType: InventoryType | undefined = $derived(
		inventoryTypes.find(({ id }) => id === selectedInventoryTypeId),
	)

	onMount(async () => {
		if (selectedInventoryTypeId) {
			await tick()
			document.getElementById(`inventory_type_id_${selectedInventoryTypeId}`)?.scrollIntoView({ block: 'center' })
		}
	})
</script>

<Table
	responsive
	stickyHeader
	filterEnabled
	columnHidingEnabled
	{columns}
	parentStyle="overflow-y: auto; max-height: 50vh;"
	idProp="id"
	rows={filteredInventoryTypes}
>
	{#snippet header()}
		<div class="d-flex align-items-end">
			<div class="btn-group me-2">
				<Button
					outline
					size="sm"
					color="success"
					iconClass="plus"
					title={translate('common:add', 'Add')}
					onclick={() => inventoryTypeModal?.add()}>{translate('common:add', 'Add')}</Button
				>

				<Button
					outline
					size="sm"
					color="primary"
					iconClass="pencil"
					title={translate('common:editInventoryType', 'Edit Inventory Type')}
					disabled={!selectedInventoryType}
					onclick={() => {
						if (selectedInventoryType) {
							inventoryTypeModal?.edit(selectedInventoryType)
						}
					}}>{translate('common:editInventoryType', 'Edit Inventory Type')}</Button
				>
			</div>
			<Checkbox
				label={translate('common:showInactive', 'Show Inactive')}
				bind:checked={showInactive}
				onchange={() => asr.go(null, { showInactive: booleanToString(showInactive) }, { inherit: true })}
			/>
		</div>
	{/snippet}
	{#snippet children({ row: inventoryTypeItem })}
		<tr
			class="cursor-pointer"
			id="inventory_type_id_{inventoryTypeItem.id}"
			class:table-primary={selectedInventoryTypeId === inventoryTypeItem.id}
			class:text-body-secondary={!inventoryTypeItem.active}
			class:fst-italic={!inventoryTypeItem.active}
			onclick={() => {
				selectedInventoryTypeId = inventoryTypeItem.id
				asr.go(null, { inventoryTypeId: inventoryTypeItem.id }, { inherit: true })
			}}
		>
			<Td property="active"><BooleanIndicator value={inventoryTypeItem.active} /></Td>
			<Td property="id">{inventoryTypeItem.id}</Td>
			<Td property="setId">{inventoryTypeItem.setId}</Td>
			<Td property="name">{inventoryTypeItem.name}</Td>
			<Td property="vehicle"><BooleanIndicator value={inventoryTypeItem.vehicle} /></Td>
			<Td property="taxCode"
				>{#if inventoryTypeItem.taxCode}{inventoryTypeItem.taxCode}{:else}
					<i class="text-body-secondary">{translate('common:notSlashAcceptable', 'N/A')}</i>{/if}</Td
			>
		</tr>
	{/snippet}
	{#snippet noRows({ visibleColumnsCount })}
		<tr>
			<td colspan={visibleColumnsCount}>
				{translate(
					'configuration.inventorytype.noMatchingInventoryTypesFound.',
					'No matching inventory types found.',
				)}</td
			>
		</tr>
	{/snippet}
</Table>

<div class="card mt-3">
	<div class="card-header">
		<h5 class="mb-0">{translate('configuration.inventorytype.inventoryTypeDetails', 'Inventory Type Details')}</h5>
	</div>
	<div class="card-body">
		<uiView></uiView>
	</div>
</div>

<InventoryTypeModal
	{asr}
	bind:this={inventoryTypeModal}
	bind:inventoryTypeList={inventoryTypes}
	inventoryTypeCreate={inventoryType => {
		selectedInventoryTypeId = inventoryType.id
	}}
/>
